// BUILD-046 increment 1b-i: how a person is shown as an INITIALS CIRCLE. What a circle SAYS (its letters, its colour, the name behind it) is decided here; PersonAvatar.tsx draws it.
//
// DISPLAY ONLY. Nothing here is stored, sent or compared: stored names, ids, assignments and permissions are not touched. Every function returns NEW values and never changes its input.
// The name comes through displayNames.personDisplay, so the "(FICTIONAL)" test marker and the "(you)" caller tag are never part of a circle, a tooltip or an accessible name.
//
// 1. INITIALS. TWO letters whenever the full name has a second word: the first letter of the first name plus the first letter of the LAST word ("Alex Stone" -> "AS"). For the pilot people the
//    role word stands in for a surname ("Julie Manager" -> "JM", "Alice Accounting" -> "AA"; Owner direction of 2026-10-21); a one-word name is one letter ("Sam" -> "S"). Upper case, LETTERS only (digits, punctuation and
//    combining marks are skipped), code-point safe (an astral letter is one letter). A name with no letter at all gives "?".
//
// 2. COLOUR. A person's colour is AVATAR_PALETTE[ fnv1a32(key) mod 12 ]. The key is the STABLE EMPLOYEE ID when the source has one (`id` or `employeeId`), so the same person is the
//    same colour on every screen and for every persona. FALLBACK when a source has no id: the key is "name:" followed by the person's full display name (marker and caller tag
//    removed, lower case), so the same name is still the same colour. The hash is FNV-1a, 32 bit, over the UTF-8 bytes of the key (offset basis 0x811c9dc5, prime 0x01000193);
//    it has published test vectors, which the unit test pins. Every pair is white text on a dark background with a WCAG contrast of at least 4.5:1 (the unit test computes the
//    ratio with the standard relative-luminance formula for every pair). The palette has 12 pairs, so two people CAN share a colour: colour is never the only signal (section 3).
//
// 3. TELLING PEOPLE APART WITHOUT COLOUR. Within one GROUP (the people shown together in one cell, one popover list or one search-result list) every distinct person gets a
//    unique visible LABEL. People are distinct by key (the same id twice is one person and has one label). When two or more distinct people have the same initials, only those
//    people are extended: first to the first 2 letters of the first name, else the first 3 ("Dana" and "Daris" -> "DAN" and "DAR"), taking the smallest length that makes them
//    different from each other AND from everyone else's label in the group; if even 3 letters do not (identical first names, or identical names), a small digit is appended to the
//    initials in the order the people appear ("A1", "A2"; "SL1", "SL2"). Digits cannot occur in initials or letter prefixes, so a digit label never clashes with another.
//
// 4. WHAT A CIRCLE SAYS. `full` is the whole name, `department` the department when the source knows it (else ''), `accessibleName` is "Full name, Department" (the circle's
//    aria-label) and `tipText` is "Full name · Department" (the tooltip and the pinned popover). People without a name or an id are left out of a group (nothing to show).
//
// Pure module: no React, no DOM, no I/O. Runtime import: displayNames.ts. Erasable TypeScript only.

import { personDisplay, stripTestMarker } from './displayNames';
import type { PersonDisplay } from './displayNames';

// ───────────────────────── the palette ─────────────────────────

export interface AvatarColor {
  /** What the colour is called (documentation and tests only; it is never drawn). */
  readonly name: string;
  readonly background: string;
  readonly text: string;
}

const WHITE = '#ffffff';

function pair(name: string, background: string): AvatarColor {
  return Object.freeze({ name, background, text: WHITE });
}

/**
 * 12 distinct pairs, all white text on a dark background. Each background is a Tailwind 700 / 800 shade, so every pair has a contrast of at least 4.5:1 (WCAG AA for text) and
 * the circle itself is far above the 3:1 non-text minimum against the white page.
 */
export const AVATAR_PALETTE: readonly AvatarColor[] = Object.freeze([
  pair('blue', '#1d4ed8'),
  pair('cyan', '#155e75'),
  pair('teal', '#0f766e'),
  pair('green', '#15803d'),
  pair('olive', '#3f6212'),
  pair('brown', '#92400e'),
  pair('orange', '#c2410c'),
  pair('red', '#b91c1c'),
  pair('pink', '#be185d'),
  pair('fuchsia', '#a21caf'),
  pair('violet', '#6d28d9'),
  pair('slate', '#475569'),
]);

// ───────────────────────── the hash ─────────────────────────

const FNV_OFFSET_BASIS = 0x811c9dc5;
const FNV_PRIME = 0x01000193;

/** FNV-1a, 32 bit, over the UTF-8 bytes of `text`, as an unsigned integer. Deterministic across runs, machines and browsers. */
export function fnv1a32(text: string): number {
  let hash = FNV_OFFSET_BASIS;
  for (const byte of new TextEncoder().encode(text)) {
    hash ^= byte;
    hash = Math.imul(hash, FNV_PRIME);
  }
  return hash >>> 0;
}

/** The colour of a person key. */
export function colorForKey(key: string): AvatarColor {
  return AVATAR_PALETTE[fnv1a32(key) % AVATAR_PALETTE.length];
}

// ───────────────────────── one person ─────────────────────────

/** A person source. Every field is optional and read defensively; a plain string is a name. `id` / `employeeId` and `name` / `displayName` are accepted so roster rows pass as they are. */
export interface AvatarPersonInput {
  id?: unknown;
  employeeId?: unknown;
  name?: unknown;
  displayName?: unknown;
  department?: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}

function textOf(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

interface Resolved {
  key: string;
  initials: string;
  /** The upper-case letters of the first name (for the longer labels). */
  letters: string[];
  display: PersonDisplay;
  department: string;
  hasIdentity: boolean;
}

/** The upper-case LETTERS of a text, one entry per letter (code-point safe). */
function upperLetters(text: string): string[] {
  const out: string[] = [];
  for (const char of Array.from(text)) if (/^\p{L}$/u.test(char)) out.push(Array.from(char.toUpperCase())[0]);
  return out;
}

function initialsOf(display: PersonDisplay, letters: readonly string[]): string {
  const first = letters[0] ?? '';
  // Two letters when the person has a second name. A real surname: short is "First L." (a trailing Jr / Sr / II / III / IV was already dropped by personDisplay), the last code point before the full stop is
  // its initial. No surname (the pilot people): the role word stands in for it, so the first letter of the last word of the full name ("Julie Manager" -> "JM"), skipping a trailing generational suffix.
  // The test marker and the caller tag are already gone from display.full.
  let surname = '';
  if (display.hasSurname) surname = Array.from(display.short.replace(/\.$/, '')).pop() ?? '';
  else if (display.short !== display.full) {
    // short differs from full only when personDisplay dropped a role word; a placeholder ("Unknown person") drops nothing, so it stays one letter and never looks like a person's initials.
    const words = display.full.split(/\s+/).filter((word) => word !== '' && !/^(?:jr|sr|ii|iii|iv)\.?$/i.test(word));
    surname = words.length > 1 ? words[words.length - 1] : '';
  }
  const surnameLetter = upperLetters(surname)[0] ?? '';
  if (first !== '') return `${first}${surnameLetter}`;
  return surnameLetter !== '' ? surnameLetter : '?';
}

function resolve(input: unknown): Resolved {
  const source: AvatarPersonInput = typeof input === 'string' ? { name: input } : isRecord(input) ? input : {};
  const name = textOf(source.name) !== '' ? textOf(source.name) : textOf(source.displayName);
  const department = stripTestMarker(source.department);
  const display = personDisplay(name, { department });
  const id = textOf(source.id) !== '' ? textOf(source.id) : textOf(source.employeeId);
  const letters = upperLetters(display.first);
  return {
    key: id !== '' ? id : `name:${display.full.toLowerCase()}`,
    initials: initialsOf(display, letters),
    letters,
    display,
    department,
    hasIdentity: id !== '' || display.full !== '',
  };
}

/** The initials of one person ("AS", "A", "?"), before any telling-apart. */
export function initialsOfPerson(person: AvatarPersonInput | string): string {
  return resolve(person).initials;
}

/** The identity key of a person: the employee id, else "name:" and the lower-case full name. */
export function avatarKeyOf(person: AvatarPersonInput | string): string {
  return resolve(person).key;
}

/** The colour of a person: the same person is the same colour wherever they are drawn. */
export function colorOfPerson(person: AvatarPersonInput | string): AvatarColor {
  return colorForKey(resolve(person).key);
}

// ───────────────────────── a group ─────────────────────────

export interface AvatarBadge {
  /** The identity key (employee id, or "name:" + lower-case full name). The same key is the same person. */
  key: string;
  /** What the circle shows. Unique among the distinct people of its group. */
  label: string;
  /** The initials before any telling-apart ("AS", "A"). */
  initials: string;
  color: AvatarColor;
  /** The whole name, marker and caller tag removed. */
  full: string;
  /** First name + last initial ("Alex S."), or the first name. */
  short: string;
  /** The department when the source knows it, else ''. */
  department: string;
  /** "Full name, Department": the circle's accessible name. */
  accessibleName: string;
  /** "Full name · Department": the tooltip and the pinned popover. */
  tipText: string;
}

/** The label of each distinct person of a group, in order of first appearance. See section 3 of the header. */
function labelsOf(distinct: readonly Resolved[]): string[] {
  const labels = distinct.map((person) => person.initials);
  const sets = new Map<string, number[]>();
  distinct.forEach((_, index) => {
    const same = sets.get(labels[index]);
    if (same === undefined) sets.set(labels[index], [index]);
    else same.push(index);
  });
  for (const [base, members] of sets) {
    if (members.length < 2) continue;
    const others = new Set(labels.filter((_, index) => !members.includes(index)));
    let chosen: string[] | null = null;
    for (const length of [2, 3]) {
      const candidate = members.map((index) => distinct[index].letters.slice(0, length).join(''));
      if (new Set(candidate).size === candidate.length && candidate.every((label) => label !== '' && !others.has(label))) {
        chosen = candidate;
        break;
      }
    }
    const final = chosen ?? members.map((_, position) => `${base}${position + 1}`);
    members.forEach((index, position) => {
      labels[index] = final[position];
    });
  }
  return labels;
}

function badgeOf(person: Resolved, label: string): AvatarBadge {
  const department = person.department;
  const full = person.display.full;
  return {
    key: person.key,
    label,
    initials: person.initials,
    color: colorForKey(person.key),
    full,
    short: person.display.short,
    department,
    accessibleName: [full, department].filter((part) => part !== '').join(', '),
    tipText: [full, department].filter((part) => part !== '').join(' \u00B7 '),
  };
}

/**
 * The circles of a group, aligned with the input by position. Distinct people (by key) have unique labels; an entry that repeats a person already in the group gets that person's
 * label. An entry with neither an id nor a name gets "?". The input is not changed.
 */
export function avatarLabels(group: readonly (AvatarPersonInput | string)[]): AvatarBadge[] {
  const list: readonly unknown[] = Array.isArray(group) ? group : [];
  const people = list.map(resolve);
  const distinct: Resolved[] = [];
  const position = new Map<string, number>();
  for (const person of people) {
    if (!position.has(person.key)) {
      position.set(person.key, distinct.length);
      distinct.push(person);
    }
  }
  const labels = labelsOf(distinct);
  return people.map((person) => badgeOf(distinct[position.get(person.key) as number], labels[position.get(person.key) as number]));
}

export interface AvatarGroupView {
  /** Every distinct person with a name or an id, in order of first appearance, labelled together. */
  people: AvatarBadge[];
  /** The first `max` people: the circles drawn in the cell. */
  shown: AvatarBadge[];
  /** The rest: behind the "+N" button. */
  rest: AvatarBadge[];
}

/** How many circles a cell draws before "+N". */
export const AVATAR_INLINE_MAX = 3;

/**
 * A group as a cell draws it: the same person listed twice is ONE circle, people with neither a name nor an id are left out, and the labels are unique across the WHOLE group (the
 * circles in the cell and the names behind "+N" never share a label).
 */
export function describeAvatarGroup(group: readonly (AvatarPersonInput | string)[], max: number = AVATAR_INLINE_MAX): AvatarGroupView {
  const list: readonly unknown[] = Array.isArray(group) ? group : [];
  const named = list.filter((person) => resolve(person).hasIdentity) as (AvatarPersonInput | string)[];
  const seen = new Set<string>();
  const people: AvatarBadge[] = [];
  for (const badge of avatarLabels(named)) {
    if (seen.has(badge.key)) continue;
    seen.add(badge.key);
    people.push(badge);
  }
  const limit = Number.isFinite(max) ? Math.max(1, Math.floor(max)) : AVATAR_INLINE_MAX;
  return { people, shown: people.slice(0, limit), rest: people.slice(limit) };
}

// ───────────────────────── sizes ─────────────────────────

export type AvatarSize = 'comfortable' | 'compact';

/** The circle's height in CSS px: 28 comfortable, 22 compact. */
export const AVATAR_PIXELS: Readonly<Record<AvatarSize, number>> = Object.freeze({ comfortable: 28, compact: 22 });
